import { HttpRequest, HttpHandlerFn } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { catchError, of, switchMap, throwError, tap } from 'rxjs'
import { AuthService } from '../auth.service'
import { TokenRefreshWorkerService } from './token-refresh-worker.service'
import {
  GraphQLErrorBody,
  GraphQLErrorCode
} from '../../models/response-graphql'
import { PlatformDetectorService } from '../../services/platform-detector/platform-detector.service'
import { AwsRumService } from '../../services/aws-rum/aws-rum.service'

/**
 * Verifica se a requisição é para uma imagem
 * @param request Requisição HTTP
 * @returns true se for uma requisição de imagem
 */
function isImageRequest(request: HttpRequest<unknown>): boolean {
  if (request.responseType === 'blob') {
    return true
  }

  const url = request.url.toLowerCase()
  const imageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.webp',
    '.svg'
  ]
  if (imageExtensions.some(ext => url.endsWith(ext))) {
    return true
  }

  const imageServicePatterns = [
    '/images/',
    '/img/',
    '/photos/',
    '/thumbnails/',
    's3.amazonaws.com',
    'cloudfront.net'
  ]
  if (imageServicePatterns.some(pattern => url.includes(pattern))) {
    return true
  }

  if (request.headers.has('X-Image-Request')) {
    return true
  }

  return false
}

export const intercept = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService)
  const router = inject(Router)
  const tokenWorkerService = inject(TokenRefreshWorkerService)
  const platformDetector = inject(PlatformDetectorService)
  const awsRumService = inject(AwsRumService)

  if (isImageRequest(request)) {
    return next(request)
  }

  const { accessToken, expiresIn } = authService.getCredentials()

  if (!accessToken) return next(request)

  setupTokenRefreshWorker(
    authService,
    router,
    tokenWorkerService,
    Number(expiresIn),
    platformDetector.isInAppWebView()
  )

  const clonedRequest = request.clone({
    setHeaders: {
      Authorization: accessToken
    }
  })

  const handleUnauthorizedError = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ) => {
    return authService.refreshToken().pipe(
      switchMap(() => {
        const { accessToken: newAccessToken, expiresIn: newExpiresIn } =
          authService.getCredentials()

        if (!newAccessToken) {
          const error = new Error('No access token')
          logError(
            awsRumService,
            error,
            'Auth Interceptor - Token Refresh',
            request
          )
          return throwError(() => error)
        }

        setupTokenRefreshWorker(
          authService,
          router,
          tokenWorkerService,
          Number(newExpiresIn),
          platformDetector.isInAppWebView()
        )

        if (isImageRequest(request)) {
          return next(request)
        }

        const newRequest = request.clone({
          setHeaders: {
            Authorization: newAccessToken
          }
        })

        return next(newRequest)
      }),
      catchError((error: any) => {
        console.error('Token refresh failed in interceptor:', error)
        logError(
          awsRumService,
          error,
          'Auth Interceptor - Token Refresh Failed',
          request
        )
        tokenWorkerService.stopTimer()
        authService.logoutImmediately()
        router.navigate(['/auth'])
        return throwError(() => error)
      })
    )
  }

  const handleGraphQLError = (
    error: GraphQLErrorBody,
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ) => {
    const enhancedError = new Error(`GraphQL Error: ${error.message}`)
    enhancedError.name = 'GraphQLError'
    Object.assign(enhancedError, {
      code: error.code,
      url: request.url,
      method: request.method,
      errorDetails: error
    })
    awsRumService.logError(enhancedError)

    switch (error.code) {
      case GraphQLErrorCode.Unauthorized:
        return handleUnauthorizedError(request, next)
      default:
        return throwError(() => error)
    }
  }

  const handleRequestResponse = (response: any) => {
    const body = response.body
    const hasError = body?.errors?.length

    if (hasError) {
      body.errors.forEach((err: any) => {
        const enhancedError = new Error(
          `Response Error: ${err.message || 'Unknown error'}`
        )
        enhancedError.name = 'ResponseError'
        Object.assign(enhancedError, {
          url: request.url,
          method: request.method,
          errorDetails: err
        })
        awsRumService.logError(enhancedError)
      })

      return throwError(() => body.errors)
    }

    return of(response)
  }

  return next(clonedRequest).pipe(
    switchMap((response: any) => handleRequestResponse(response)),
    catchError((error: GraphQLErrorBody[] | any) => {
      if (!Array.isArray(error)) {
        logError(awsRumService, error, 'Non-GraphQL Error', request)
        return throwError(() => error)
      }

      const [firstError] = error

      return handleGraphQLError(firstError, request, next)
    })
  )
}

/**
 * Função auxiliar para registrar erros no AWS RUM
 */
function logError(
  awsRumService: AwsRumService,
  error: any,
  context: string,
  request?: HttpRequest<unknown>
) {
  let errorToLog: Error

  if (error instanceof Error) {
    errorToLog = error
  } else if (typeof error === 'string') {
    errorToLog = new Error(error)
  } else {
    try {
      errorToLog = new Error(JSON.stringify(error))
    } catch (e) {
      errorToLog = new Error('Unknown error format')
    }
  }

  if (request) {
    Object.assign(errorToLog, {
      url: request.url,
      method: request.method,
      headers: request.headers
        .keys()
        .reduce((acc: Record<string, string>, key: string) => {
          if (key.toLowerCase() !== 'authorization') {
            acc[key] = request.headers.get(key) || ''
          }
          return acc
        }, {})
    })
  }

  awsRumService.logError(errorToLog)
}

function setupTokenRefreshWorker(
  authService: AuthService,
  router: Router,
  tokenWorkerService: TokenRefreshWorkerService,
  expiresIn: number,
  isInAppWebView = false
) {
  if (!expiresIn || expiresIn <= 0 || isInAppWebView) return

  tokenWorkerService.setRefreshCallback(() => {
    console.log('Executando refresh automático via worker...')

    authService
      .refreshToken()
      .pipe(
        tap(() => {
          console.log('Token refreshed automaticamente com sucesso via worker')
          const { expiresIn: newExpiresIn } = authService.getCredentials()

          tokenWorkerService.startTimer(Number(newExpiresIn))
        }),
        catchError(error => {
          console.error('Erro no refresh automático via worker:', error)

          const awsRumService = inject(AwsRumService)
          const enhancedError = new Error(
            `Token refresh worker error: ${error?.message || 'Unknown error'}`
          )
          enhancedError.name = 'TokenRefreshError'
          awsRumService.logError(enhancedError)

          tokenWorkerService.stopTimer()
          authService.logoutImmediately()
          router.navigate(['/auth'])
          return throwError(() => error)
        })
      )
      .subscribe()
  })

  tokenWorkerService.startTimer(expiresIn / 2)
}
